// Util de telefone brasileiro (DDD + número).
//
// O código do país NÃO faz parte do dado do cadastro: a API grava DDD e número em
// colunas separadas (`ddd_celular` com 2 caracteres, `numero_celular` com 10). Como
// `react-input-mask` encaixa os dígitos por posição, um valor com "+55" — colado do
// WhatsApp ou preenchido pelo autofill do navegador — desloca tudo: "+55 (11)
// 99281-2098" vira "(55) 11992-8120", com o 55 no lugar do DDD e os dois últimos
// dígitos descartados. Por isso o código do país é removido ANTES de a máscara
// aplicar, em `stripCountryCodeOnMask`.

export const onlyDigits = (value?: string | null): string => String(value ?? '').replace(/\D/g, '');

// Dígitos do telefone sem o código do país. A remoção só acontece quando sobram 10
// (fixo) ou 11 (celular) dígitos: o DDD 55 (Santa Maria/RS) é legítimo e
// "(55) 99281-2098" — 11 dígitos — não pode ser confundido com o +55.
export const phoneDigits = (value?: string | null): string => {
  let digits = onlyDigits(value);

  // Prefixo internacional discado (0055).
  if (digits.length === 14 || digits.length === 15) {
    digits = digits.replace(/^00/, '');
  }

  if (digits.length === 12 || digits.length === 13) {
    digits = digits.replace(/^55/, '');
  }

  return digits;
};

// Formata no padrão das máscaras do cadastro: `(DD) NNNNN-NNNN` para celular e
// `(DD) NNNN-NNNN` para fixo. `maxDigits` limita ao tamanho do campo.
export const maskBrPhone = (value?: string | null, maxDigits = 11): string => {
  const digits = phoneDigits(value).slice(0, maxDigits);
  const ddd = digits.slice(0, 2);
  const numero = digits.slice(2);

  if (!ddd) return '';
  if (!numero) return `(${ddd}`;

  const cut = numero.length > 8 ? 5 : 4;

  return numero.length > cut
    ? `(${ddd}) ${numero.slice(0, cut)}-${numero.slice(cut)}`
    : `(${ddd}) ${numero}`;
};

// DDD + número já formatado, no formato que a API espera em `contact`/`client`.
// Devolve vazio quando não há telefone completo (10 ou 11 dígitos).
export const splitBrPhone = (value?: string | null): { ddd: string; numero: string } => {
  const digits = phoneDigits(value);

  if (digits.length !== 10 && digits.length !== 11) {
    return { ddd: '', numero: '' };
  }

  const masked = maskBrPhone(digits, digits.length);

  return { ddd: digits.slice(0, 2), numero: masked.slice(masked.indexOf(' ') + 1) };
};

export const isCelularValid = (value?: string | null): boolean => phoneDigits(value).length === 11;

export const isTelefoneValid = (value?: string | null): boolean => phoneDigits(value).length === 10;

interface MaskState {
  value: string;
  selection: { start: number; end: number } | null;
}

interface MaskConfig {
  mask: string;
  permanents: number[];
}

/**
 * Handler para o `beforeMaskedValueChange` do `react-input-mask` (2.x): quando o
 * valor entrando no campo traz o código do país, a máscara já deslocou os dígitos —
 * então o valor é remontado a partir do que o usuário realmente digitou/colou
 * (`userInput`), sem o +55. Nos demais casos o comportamento da máscara é mantido.
 */
export const stripCountryCodeOnMask = (
  newState: MaskState,
  oldState: MaskState,
  userInput: string | null,
  config: MaskConfig,
): MaskState => {
  if (!userInput) return newState;

  const entered = onlyDigits(userInput);
  const normalized = phoneDigits(entered);

  // Sem código do país não há nada a corrigir: a máscara já fez o certo.
  if (normalized === entered) return newState;

  const maxDigits = config.mask.length - config.permanents.length;
  const value = maskBrPhone(normalized, maxDigits);

  return { value, selection: { start: value.length, end: value.length } };
};

// Props para espalhar no `<InputMask>` dos campos de telefone. Os @types instalados
// descrevem a 3.x (`beforeMaskedStateChange`), mas a versão em uso é a 2.0.4
// (`beforeMaskedValueChange`) — o prop entra por spread, em um único lugar.
export const countryCodeSafeMaskProps = {
  beforeMaskedValueChange: stripCountryCodeOnMask,
};
